// src/utils/pwaInstall.js
// Capture de l'événement `beforeinstallprompt` + détection plateforme + cadence
// des rappels d'installation PWA (mur souple mobile web).
//
// IMPORTANT : ce module DOIT être importé très tôt (index.js) car
// `beforeinstallprompt` est émis par Chrome/Android peu après le chargement,
// souvent avant que React ne soit monté. On l'attrape au niveau module et on
// le rejoue via un CustomEvent pour la couche React.

// --- Capture du prompt natif (Android / Chromium) --------------------------

let deferredPrompt = null

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Empêcher la mini-infobar Chrome par défaut : on gère l'UI nous-mêmes.
    e.preventDefault()
    deferredPrompt = e
    window.dispatchEvent(new CustomEvent('pwa-install-available'))
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    markInstalled()
    window.dispatchEvent(new CustomEvent('pwa-installed'))
  })
}

/** Y a-t-il un prompt d'installation natif disponible (Android/Chromium) ? */
export const hasNativePrompt = () => deferredPrompt !== null

/**
 * Déclenche le prompt d'installation natif. Retourne 'accepted' | 'dismissed'
 * | 'unavailable'. À n'appeler que dans un gestionnaire de clic utilisateur.
 */
export const promptNativeInstall = async () => {
  if (!deferredPrompt) return 'unavailable'
  try {
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    deferredPrompt = null
    if (outcome === 'accepted') markInstalled()
    return outcome // 'accepted' | 'dismissed'
  } catch (err) {
    console.warn('[PWA] Échec du prompt natif:', err)
    return 'unavailable'
  }
}

// --- Détection plateforme ---------------------------------------------------

const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '')

/** iOS (iPhone/iPad/iPod), y compris iPadOS qui se déguise en Mac. */
export const isIOS = () => {
  const s = ua()
  if (/iphone|ipad|ipod/i.test(s)) return true
  // iPadOS 13+ se présente comme "MacIntel" mais avec un écran tactile
  return (
    typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1
  )
}

export const isAndroid = () => /android/i.test(ua())

/**
 * L'app est-elle lancée en mode standalone (installée sur l'écran d'accueil),
 * plutôt que dans un onglet Safari/Chrome classique ? Détection synchrone
 * (pas de state React) pour un usage hors composant, ex: choisir le message
 * d'erreur adapté au moment d'un refus de géolocalisation.
 *
 * Sur iOS, une PWA standalone a sa PROPRE entrée dans Réglages > [nom de
 * l'app] > Position, distincte du réglage Safari par site — un refus ou une
 * entrée désactivée y fait échouer `getCurrentPosition` immédiatement en
 * PERMISSION_DENIED, sans jamais afficher le prompt natif. « Vérifiez les
 * réglages de votre navigateur » est alors trompeur : il n'y a pas de
 * navigateur visible sur une app lancée depuis l'écran d'accueil.
 */
export const isStandalone = () =>
  typeof window !== 'undefined' &&
  Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true ||
      document.referrer?.includes('android-app://'),
  )

/**
 * Navigateur intégré à une app tierce (WhatsApp, Instagram, Facebook,
 * Messenger, LinkedIn, TikTok, Snapchat, WeChat…). Dans ces webviews,
 * l'installation d'une PWA est IMPOSSIBLE : il faut rouvrir dans le navigateur.
 */
export const isInAppBrowser = () => {
  const s = ua()
  // Réseaux sociaux & messageries les plus courants (surtout diaspora)
  if (/FBAN|FBAV|FB_IAB|Instagram|Messenger|MicroMessenger|Line\/|LinkedInApp|TikTok|Snapchat|Twitter/i.test(s)) {
    return true
  }
  // Webview Android générique : "; wv)" dans l'UA
  if (isAndroid() && /; wv\)/i.test(s)) return true
  // iOS : navigateur qui n'est ni Safari, ni Chrome/Firefox iOS déclarés
  if (isIOS()) {
    const isRealSafari = /Safari/i.test(s) && !/CriOS|FxiOS|EdgiOS/i.test(s)
    const isKnownBrowser = /CriOS|FxiOS|EdgiOS/i.test(s)
    if (!isRealSafari && !isKnownBrowser) return true
  }
  return false
}

/**
 * Renvoie la stratégie d'installation adaptée au contexte courant :
 *  - 'native'  : Android/Chromium avec prompt disponible → 1 tap
 *  - 'ios'     : iOS Safari → instructions "Ajouter à l'écran d'accueil"
 *  - 'in_app'  : webview tierce → rouvrir dans le navigateur
 *  - 'manual'  : autre cas (Android sans prompt encore émis, etc.)
 */
export const getInstallStrategy = () => {
  if (isInAppBrowser()) return 'in_app'
  if (hasNativePrompt()) return 'native'
  if (isIOS()) return 'ios'
  return 'manual'
}

// --- Cadence des rappels (localStorage) ------------------------------------

const STORAGE_KEY = 'pwa_install_wall'
// Rappels espacés : immédiat → +7j → +30j → stop.
const REMINDER_DELAYS_MS = [0, 7 * 24 * 3600 * 1000, 30 * 24 * 3600 * 1000]

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    return {}
  }
}

const writeState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* quota / mode privé : on ignore silencieusement */
  }
}

/** Marque l'app comme installée : le mur ne réapparaîtra plus jamais. */
export const markInstalled = () => {
  writeState({ ...readState(), installed: true })
}

/** L'utilisateur a fermé le mur : on incrémente et on horodate. */
export const recordDismiss = () => {
  const state = readState()
  writeState({
    ...state,
    dismissCount: (state.dismissCount || 0) + 1,
    dismissedAt: Date.now(),
  })
}

/**
 * Le mur doit-il s'afficher maintenant ? (cadence uniquement — les gardes
 * mobile/PWA/auth/route sont gérées par l'appelant).
 */
export const shouldShowWall = () => {
  const state = readState()
  if (state.installed) return false

  const count = state.dismissCount || 0
  // Après 3 refus (immédiat + 7j + 30j), on n'insiste plus.
  if (count >= REMINDER_DELAYS_MS.length) return false

  // Jamais montré encore
  if (!state.dismissedAt) return true

  // Assez de temps écoulé depuis le dernier refus ?
  const nextDelay = REMINDER_DELAYS_MS[count]
  return Date.now() - state.dismissedAt >= nextDelay
}

export default {
  hasNativePrompt,
  promptNativeInstall,
  isIOS,
  isAndroid,
  isInAppBrowser,
  isStandalone,
  getInstallStrategy,
  markInstalled,
  recordDismiss,
  shouldShowWall,
}
